import type { TrackedTask } from '@/lib/api/tasks.js';

/**
 * The address of the Tasks page: the Board view (the list is the default and is left out), the task
 * open in the panel, and the filters.
 */
export interface TasksSearch {
  view?: 'board';
  task?: string;
  source?: string;
  priority?: string;
  author?: string;
  q?: string;
}

const TEXT_KEYS = ['task', 'source', 'priority', 'author', 'q'] as const;

/** Keeps the Board view and every non-empty text value the page knows; drops the rest. */
export function parseTasksSearch(raw: Record<string, unknown>): TasksSearch {
  const search: TasksSearch = raw.view === 'board' ? { view: 'board' } : {};
  for (const key of TEXT_KEYS) {
    const value = raw[key];
    if (typeof value === 'string' && value.length > 0) search[key] = value;
  }
  return search;
}

/** Whether any filter narrows the tasks; the view and the open task do not. */
export function hasFilters(search: TasksSearch): boolean {
  return [search.source, search.priority, search.author, search.q].some(
    (value) => value !== undefined,
  );
}

/** Whether a task's words hold the search text, ignoring case. */
function matchesText(task: TrackedTask, text: string): boolean {
  const needle = text.trim().toLocaleLowerCase();
  return [task.summary, task.placeLabel, task.author, task.excerpt].some(
    (value) => value?.toLocaleLowerCase().includes(needle) ?? false,
  );
}

/** The tasks that pass every filter set, in their order. */
export function filterTasks(
  tasks: readonly TrackedTask[],
  search: TasksSearch,
): TrackedTask[] {
  return tasks.filter(
    (task) =>
      (search.source === undefined || task.source === search.source) &&
      (search.priority === undefined || task.priority === search.priority) &&
      (search.author === undefined || task.author === search.author) &&
      (search.q === undefined || matchesText(task, search.q)),
  );
}
